import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  CollectionPreferences,
  CollectionPreferencesProps,
  Pagination,
  PropertyFilter,
  Table,
} from '@amzn/awsui-components-react-v3';
import {
  defaultWrapLinesPreference,
  i18nStrings,
  mediumPageSizePreference,
  paginationLabels,
} from 'src/commons/tables';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';

import { disassociateTagFromResources, getResourceTags } from 'src/api/permissions';
import { TagEditModal } from 'src/components/workspaces/dataBrowse/dataset/editTagModal';
import { DATASET_RESOURCE_TYPE, LAKEFORMATION_TAG_TYPE, REDSHIFT_TAG_TYPE } from 'src/commons/constants';
import { EmptyState } from 'src/commons/EmptyState';

import { PageHeader } from 'src/components/common/PageHeader';
import { TagsList } from 'aws-sdk/clients/awsdatalakegladstonelambda';
import { GetDataSetResponseStructure } from 'aws-sdk/clients/awsdlhybridcatalogservicelambda';

export interface TagListProps {
  setContentType: any;
  activeGroup: string;
  username: string;
  activeWorkspace: any;
  dataset: GetDataSetResponseStructure;
  initialTags?: TagsList;
  updateTagLabel: any;
  notifyEditFailure: any;
  notifyEditSuccess: any;
}

export const DatasetTagList = (props: TagListProps) => {
  const [allItems, setItems] = useState<TagsList>(props.initialTags ?? []);
  const [loadingTags, setLoadingTags] = useState(false);
  const [buttonLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(undefined);
  const [addTagVisible, setAddTagVisible] = useState(false);
  const [ownDataset, setOwnDataset] = useState(false);
  const [resource, setResource] = useState(undefined);

  const fetchTags = async () => {
    let tagList = [];
    let getDatasetTagsResponse;
    let getInheritedTagsResponse;
    try {
      getDatasetTagsResponse = await getResourceTags({
        resourceId: props.dataset?.Id,
      });
    } catch (err) {
      console.log(err);
    }
    try {
      if (props.dataset.ClusterIdentifier != null) {
        getInheritedTagsResponse = await getResourceTags({
          resourceId: `DS-redshift|A-${props.dataset?.IdInfo?.CatalogId}|CI-${props.dataset?.IdInfo?.ClusterIdentifier}|DN-${props.dataset?.IdInfo?.DatabaseName}|SN-${props.dataset?.IdInfo?.SchemaName}|R-${props.dataset?.IdInfo?.Region}`,
        });
      } else if (props.dataset.BucketName != null) {
        getInheritedTagsResponse = await getResourceTags({
          resourceId: `DS-s3table|A-${props.dataset?.IdInfo?.CatalogId}|BN-${props.dataset?.IdInfo?.BucketName}|DN-${props.dataset?.IdInfo?.DatabaseName}|R-${props.dataset?.IdInfo?.Region}`,
        });
      } else {
        getInheritedTagsResponse = await getResourceTags({
          resourceId: `DS-glueLF|A-${props.dataset?.IdInfo?.CatalogId}|DN-${props.dataset?.IdInfo?.DatabaseName}|R-${props.dataset?.IdInfo?.Region}`,
        });
      }
    } catch (err) {
      console.log(err);
    }

    let tagSet = new Set();
    if (getInheritedTagsResponse != null) {
      for (let tag of getInheritedTagsResponse?.tags) {
        tagList.push(tag);
        tagSet.add(tag.tagPair);
      }
    }
    if (getDatasetTagsResponse != null) {
      for (let tag of getDatasetTagsResponse?.tags) {
        if (!tagSet.has(tag.tagPair)) {
          tagList.push(tag);
        }
      }
    }
    setItems(tagList);
  };

  const handleRefresh = async (initialLoad: boolean) => {
    setLoadingTags(true);
    if (!initialLoad || !props.initialTags) {
      await fetchTags();
    }
    if (props.dataset?.ClusterIdentifier != null) {
      setResource({
        schemaName: props.dataset?.IdInfo?.SchemaName,
        clusterId: props.dataset?.IdInfo?.ClusterIdentifier,
        catalogId: props.dataset?.IdInfo?.CatalogId,
        resourceType: DATASET_RESOURCE_TYPE,
        resourceName: props.dataset?.IdInfo?.TableName,
        databaseName: props.dataset?.IdInfo?.DatabaseName,
      });
    } else {
      setResource({
        resourceType: DATASET_RESOURCE_TYPE,
        resourceName: props.dataset?.IdInfo?.TableName,
        resourceId: props.dataset?.Id,
      });
    }

    setOwnDataset(props.activeWorkspace != null && props.dataset?.Owners.includes(props.activeWorkspace.workspaceId));
    setLoadingTags(false);
  };

  useEffect(() => {
    handleRefresh(true);
  }, []);

  const [preferences, setPreferences] = useState<CollectionPreferencesProps.Preferences>({
    wrapLines: false,
    pageSize: 15,
  });

  const columnDefinitions: TableProps.ColumnDefinition<any>[] = [
    {
      id: 'tagKey',
      header: 'Key',
      cell: (item) => item?.tagKey,
      minWidth: 200,
    },
    {
      id: 'tagValue',
      header: 'Value',
      cell: (item) => item?.tagValue,
      minWidth: 200,
    },
    {
      id: 'detail',
      header: 'Detail',
      cell: (item) => (item.source == undefined ? '' : `Inherited from database ${item.source}`),
      minWidth: 200,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (item) => item?.status,
      minWidth: 200,
    },
    {
      id: 'statusReason',
      header: 'Status reason',
      cell: (item) => item?.statusReason,
      minWidth: 200,
    },
  ];

  const handleAction = async (e) => {
    if (e.detail.id == 'add') {
      setAddTagVisible(true);
    } else if (e.detail.id == 'delete') {
      await handleRemove();
      fetchTags();
      props.updateTagLabel();
    }
  };

  const handleRemove = async () => {
    try {
      let request = {
        tagPair: selectedItem?.tagPair,
        catalogId: selectedItem?.catalogId,
        region: props.dataset?.IdInfo.Region,
        type: props.dataset.ClusterIdentifier != null ? REDSHIFT_TAG_TYPE : LAKEFORMATION_TAG_TYPE,
        resources: [resource],
      };
      let response = await disassociateTagFromResources(request);
      if (response.succeeded) {
        props.notifyEditSuccess(response.message);
      } else {
        props.notifyEditFailure(response.message);
      }
    } catch (err) {
      props.notifyEditFailure('Tag remove failure', err);
    }
  };

  const { items, collectionProps, paginationProps, propertyFilterProps, filteredItemsCount } = useCollection(allItems, {
    filtering: {
      empty: <EmptyState title='No tags attached' subtitle='No tags to display.' />,
    },
    pagination: { pageSize: preferences.pageSize },
    sorting: {
      defaultState: {
        isDescending: true,
        sortingColumn: {
          sortingField: 'LastCommentTime',
        },
      },
    },
    selection: {},
    propertyFiltering: {
      filteringProperties: [
        {
          propertyLabel: 'Key',
          key: 'tagKey',
          groupValuesLabel: 'Keys',
        },
        {
          propertyLabel: 'Value',
          key: 'tagValue',
          groupValuesLabel: 'Values',
        },
      ],
    },
  });

  useEffect(() => {
    const { selectedItems } = collectionProps;
    if (!selectedItems.length) return;
    setSelectedItem(selectedItems[selectedItems.length - 1]);
  }, [collectionProps.selectedItems]);

  return (
    <>
      <Table
        {...collectionProps}
        loadingText='Loading tags...'
        loading={loadingTags}
        columnDefinitions={columnDefinitions}
        items={items}
        wrapLines={preferences.wrapLines}
        resizableColumns={true}
        header={
          <>
            {ownDataset ? (
              <PageHeader
                buttons={[
                  {
                    text: '',
                    icon: 'refresh',
                    onItemClick: () => handleRefresh(false),
                  },
                  {
                    text: 'Actions',
                    onItemClick: (e) => handleAction(e),
                    items: [
                      {
                        text: 'Add',
                        id: 'add',
                      },
                      {
                        text: 'Delete',
                        id: 'delete',
                        disabled:
                          !collectionProps.selectedItems.length ||
                          collectionProps.selectedItems[0]?.source !== undefined,
                      },
                    ],
                    loading: buttonLoading,
                  },
                ]}
                header={'Tags'}
              />
            ) : (
              <PageHeader
                buttons={[
                  {
                    text: '',
                    icon: 'refresh',
                    onItemClick: () => handleRefresh(false),
                  },
                ]}
                counter={`(${allItems.length})`}
                header={'Tags'}
              />
            )}
          </>
        }
        selectionType={ownDataset ? 'single' : undefined}
        pagination={<Pagination {...paginationProps} ariaLabels={paginationLabels} />}
        preferences={
          <CollectionPreferences
            title={'Preferences'}
            confirmLabel={'Confirm'}
            cancelLabel={'Cancel'}
            preferences={preferences}
            onConfirm={({ detail }) => setPreferences(detail)}
            pageSizePreference={mediumPageSizePreference}
            wrapLinesPreference={defaultWrapLinesPreference}
          />
        }
        empty={<EmptyState title='No tags attached' subtitle='No tags to display.' />}
        filter={
          <PropertyFilter
            {...propertyFilterProps}
            disabled={loadingTags}
            i18nStrings={i18nStrings}
            countText={`${filteredItemsCount} ${filteredItemsCount === 1 ? 'match' : 'matches'}`}
          />
        }
      />
      {addTagVisible && (
        <TagEditModal
          {...props}
          visible={addTagVisible}
          tagType={props.dataset?.ClusterIdentifier != null ? REDSHIFT_TAG_TYPE : LAKEFORMATION_TAG_TYPE}
          dismiss={() => {
            setAddTagVisible(false);
          }}
          currentTags={allItems}
          resource={resource}
          refresh={fetchTags}
        />
      )}
    </>
  );
};
